import { useState, useRef } from "react";
import {CapitalInvestment, Construction, LicenceDetails, Summary, Workforce} from "../fragments/icons";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";
import {PaymentCategory, useCreateInvestment} from "../../repositories/licences-repository";
import { generateControlNumber } from "../../services/applications-services";
import type { SubmitPaymentChoice } from "./mobile-money-payment-form";
import {INVESTMENT_APPLICATION_FEE, investmentInspectionFee} from "../../constants/fees";

import VerticalStepper from "../fragments/vertical-stepper";
import Button from "@/components/buttons/Button";
import SuccessfulApplicationCard from "../fragments/application-succefully-card";
import ProjectForm from "./project-details-form";

import SummaryInvestmentDetailsForm from "./summary-investment-details-form";
import PaymentMethodsForms from "./payment-methods-forms";
import CapitalInvestmentForm from "./capital-investment-form";
import WorkForcesForm from "./work-forces-form";
import LandConstractionsForm from "./land-constractions-form";
import toast from "react-hot-toast";


const InvestmentApplicationForm = () => {
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [open, setOpen] = useState<boolean>(false);
    const { mutateAsync, isPending } = useCreateInvestment();

    // See the licence wizard: the payment choice is read inside the submit handler, not during
    // render, so it lives in a ref.
    const choiceRef = useRef<SubmitPaymentChoice>({ payLater: true });
    const [controlNumber, setControlNumber] = useState<string | null>(null);
    const [isIssuing, setIsIssuing] = useState(false);
    const [paymentCompleted, setPaymentCompleted] = useState(false);

    const handlePaymentSuccess = () => {
        setPaymentCompleted(true);
    };

    const steps = [
        {
            step: 1,
            title: "Application Fee",
            isActive: currentStep === 1,
            isCompleted: currentStep > 1,
            image: <LicenceDetails isCompleted={currentStep > 1} isActive={currentStep === 1} />,
        },
        {
            step: 2,
            title: "Project Details",
            isActive: currentStep === 2,
            isCompleted: currentStep > 2,
            image: <LicenceDetails isCompleted={currentStep > 2} isActive={currentStep === 2} />,
        },
        {
            step: 3,
            title: "Capital Investment",
            isActive: currentStep === 3,
            isCompleted: currentStep > 3,
            image: <CapitalInvestment isCompleted={currentStep > 3} isActive={currentStep === 3} />,
        },
        {
            step: 4,
            title: "Construction ",
            isActive: currentStep === 4,
            isCompleted: currentStep > 4,
            image: <Construction isCompleted={currentStep > 4} isActive={currentStep === 4} />,
        },
        {
            step: 5,
            title: "Work Forces",
            isActive: currentStep === 5,
            isCompleted: currentStep > 5,
            image: <Workforce isCompleted={currentStep > 5} isActive={currentStep === 5} />,
        },
        {
            step: 6,
            title: "Submission",
            isActive: currentStep === 6,
            isCompleted: currentStep >= 6,
            image: <Summary isActive={currentStep === 6} />,
        },
    ]

    const handleNext = async () => {
        if (currentStep === 1 && !paymentCompleted) {
            toast.error("Please make payment first");
            return;
        }
        const isValid = await trigger(
            [
                "projectName",
                "projectLocation",
                "investMentAmount",
                "estimatedInvestment",
                "personalContribution",
                "loan",
                "physicalLocation",
                "sourceOfFunding",
                "projectActivities",
                "numberOfRooms",
                "estimatedInvestmentAmount",
                "personalContribution",
                "loanAmount",
                "institutionProvidingLoanName",
                "bankName",
                "bankAddress",
                "emailAddress",
                "zone",
                "region",
                "district",
                "street",
                "municipal",
                "poBox",
                "postCode"
            ]);
        if (isValid) {
            if (currentStep < steps.length) {
                setCurrentStep((prevStep) => prevStep + 1);
            }
        }
    }

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prevStep) => prevStep - 1);
        }
    }
    const methods = useForm<any>({
        defaultValues: {

        },
    })
    
    const {  trigger } = methods;



    const projectName = methods.getValues("projectName");
    const projectLocation = methods.getValues("projectLocation");
    const investMentAmount = methods.getValues("estimatedInvestment");
    const sourceOfFunding = methods.getValues("sourceOfFunding");
    const personalContribution = methods.getValues("personalContribution");
    const loanAmount = methods.getValues("loan");
    const institutionProvidingLoanName = methods.getValues("institutionName");
    const bankName = methods.getValues("bankName");
    const bankAddress = methods.getValues("bankAddress");
    const zone = methods.getValues("zone");

    // Fees are client-side constants - see constants/fees.ts. The application
    // fee is charged at step 1, the inspection fee at submission.
    const inspectionFee = investmentInspectionFee(zone);
    const totalFees = INVESTMENT_APPLICATION_FEE + inspectionFee;


    const handleModalSubmit = (choice: SubmitPaymentChoice) => {
        choiceRef.current = choice;
        methods.handleSubmit(onSubmit)();
       
        setTimeout(() => {
            setShowPaymentForm(false)
        }, 5000);
  };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <PaymentMethodsForms
                        header="Investment Application Fee"
                        baseFee={`${INVESTMENT_APPLICATION_FEE}`}
                        totalFees={`${INVESTMENT_APPLICATION_FEE}`}
                        onSubmit={handlePaymentSuccess}
                        paymentCategory={PaymentCategory.RESIDENCYFEE}
                    />
                );
            case 2:
                return (
                    <ProjectForm />
                );
            case 3:
                return (
                    <CapitalInvestmentForm />
                );
            case 4:
                return (
                    <LandConstractionsForm />
                );
            case 5:
                return (
                    <WorkForcesForm />
                );
            case 6:
                return (
                    <SummaryInvestmentDetailsForm
                        baseFee={`${INVESTMENT_APPLICATION_FEE}`}
                        inspectionFee={`${inspectionFee}`}
                        totalFees={`${totalFees}`}
                        zone={zone}
                        projectName={projectName}
                        projectLocation={projectLocation}
                        investMentAmount={investMentAmount.amount}
                        sourceOfFunding={sourceOfFunding}
                        personalContribution={personalContribution.amount}
                        loanAmount={loanAmount.amount}
                        institutionProvidingLoanName={institutionProvidingLoanName}
                        bankName={bankName} bankAddress={bankAddress}
                        showPaymentForm={showPaymentForm}
                        setShowPaymentForm={setShowPaymentForm}
                        handleModalSubmit={handleModalSubmit}
                    />
                );
            default:
                return null;
        }
    }

    const onSubmit: SubmitHandler<any> = async (data) => {    
        
        const mappedData = {
          businessDetails: {
            projectName: data.projectName || "",
            projectNumber: data.projectNumber || "",
            dateIssued: data.dateIssued || "",
            businessName: data.companyName || "",
            postCode: data.postCode || "",
            poBox: data.projectPoBox || "",
            projectActivities: data.projectActivities || [""],
            numberOfRooms: data.numberOfRooms || 0,
          },
          capitalInvestment: {
            companyOperatingProject: data.companyOperatingProject || "",
            estimatedInvestmentAmount: data.estimatedInvestment || "",
            currency: data.currency || "",
            personalContribution: data.personalContribution || "",
            loanAmount: data.loan || "",
            institutionProvidingLoan: data.institutionName || "",
            bankName: data.bankName || "",
            bankAddress: data.bankAddress || "",
            shareholders: data.shareHolders.map((shareholder: any) => ({
              salutation: shareholder.salutation || "",
              firstName: shareholder.firstName || "",
              middleName: shareholder.middleName || "",
              lastName: shareholder.lastName || "",
              nationality: shareholder.nationality || "",
              numberOfShares: shareholder.numberOfShares || "",
            })),
          },
          construction: {
            landSizeInHectares: data.landSizeHectares || "",
            existingBuildingsUsed: data.existingBuildings || "",
            constructionCompletionTime: data.constructionCompletionTime || "",
            projectLifespanYears: data.projectLifespan || "",
            environmentalManagementPlans: data.environmentalManagementPlanDescription || "",
          },
          workforce: {
            localPermanentEmployees: data.localPermanentEmployees || "",
            localTemporaryEmployees: data.localTemporaryEmployees || "",
            foreignPermanentEmployees: data.foreignPermanentEmployees || "",
            foreignTemporaryEmployees: data.foreignTemporaryEmployees || "",
          },
        };
        const choice = choiceRef.current;

        // useCreateInvestment surfaces its own failure toast; without an application there is
        // nothing to bill, so stop rather than raise an orphaned bill.
        const created = await mutateAsync(mappedData).catch(() => null);
        if (!created) return;

        setShowPaymentForm(false);
        setControlNumber(null);
        setOpen(true);

        if (choice.payLater) return;

        // The bill is raised only once the application exists — its control number is stored
        // against the applicationId the API returns here.
        const applicationId = (created as any)?.results?.applicationId;
        setIsIssuing(true);
        try {
            const bill: any = await generateControlNumber({
                applicationId,
                amount: Number(totalFees ?? 0),
                phoneNumber: choice.phoneNumber as string,
                provider: choice.provider as any,
                custName: data.companyName || "",
                billDesc: "Investment application fee",
            });
            setControlNumber(bill?.results?.controlNumber ?? null);
        } catch {
            setControlNumber(null);
        } finally {
            setIsIssuing(false);
        }
    };
    


    
    return (
        <div className="w-full">
            <>
                <SuccessfulApplicationCard
                    isOpen={open}
                    onClose={() => setOpen(false)}
                    backTo="/applications/investments"
                    controlNumber={controlNumber}
                    isIssuing={isIssuing}
                    payLater={choiceRef.current.payLater}
                />
            </>
            <div className="flex justify-between items-center border-b border-gray-200 py-2  px-4">
                <div>
                    <span>Submit New Applications</span>
                </div>
                <div className="flex justify-between items-center gap-3">
                    <Button
                        type="button"
                        intent="default"
                        size="md"
                        className={`${!paymentCompleted && "opacity-50 pointer-events-none"} `}
                        onClick={handleBack}>
                        Back
                    </Button>
                    <Button
                        disabled={!paymentCompleted}
                        type="button"
                        intent="primary"
                        className={`${currentStep === steps.length && "hidden"} ${!paymentCompleted && "opacity-50 pointer-events-none"} `}
                        size="md" onClick={handleNext}>
                        Continue
                    </Button>
                </div>
            </div>
            <div className="flex space-x-5 gap-5 px-4 ">
                <div>
                    <VerticalStepper
                        steps={steps}
                    />
                </div>
                <div className="mt-2 ml-10 w-full max-w-3xl">
                    <FormProvider {...methods}>
                        <form
                            className="w-full">
                            {renderStepContent()}
                            <div className='flex justify-between w-full'>
                                <div></div>
                                {currentStep === steps.length &&
                                    <Button
                                            onClick={()=>setShowPaymentForm(true)}
                                            type="button"
                                            intent="primary"
                                            loading={isPending}
                                            size="md">
                                        Submit
                                    </Button>
                                }
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
};

export default InvestmentApplicationForm